import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowDownToLine, Loader2, RotateCw, Send, Sparkles, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useAssistantName } from '@/lib/use-assistant-name'
import type { EricSurface } from '@/lib/eric-surface'

/**
 * Inline Eric drawer — opens from any `<ChatInput>`'s Eric button and
 * lets the mechanic talk to Eric without leaving the page they're on.
 *
 * Modality (per Q5 recommendation):
 *   - mobile (< md): `<Sheet side="bottom">` — takes ~70% of the
 *     viewport, keyboard-friendly, swipe-to-dismiss
 *   - desktop (≥ md): `<Popover>` anchored to the Eric button — light-
 *     weight overlay, dismisses on outside click
 *
 * Three starter actions (Help write / Summarize / Look up) seed an
 * editable prompt. The mechanic can edit before sending. Replies
 * render in the drawer with optional Insert/Replace buttons — Eric
 * never modifies the textarea silently (CLAUDE.md trust contract).
 *
 * Wire format: POST /api/eric/ask with the new optional `surface`
 * field — the brain uses it to default tool args. See worker
 * /lib/eric-brain.ts EricSurface type for the wire shape (mirrored
 * in src/lib/eric-surface.ts).
 */

type EricStarter = 'help_write' | 'summarize' | 'lookup'

type AskResponse = {
  reply: string
  guardrail_decision: string
  tool_calls: unknown[]
  ms_total: number
  error: string | null
}

export type EricInlineDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  surface: EricSurface
  /** Snapshot of what's in the host ChatInput right now. Passed in so
   *  Eric can reference the draft when the mechanic asks "clean up
   *  what I typed." */
  currentText: string
  /** Kept on the API for symmetry with a possible future popover
   *  modality; currently unused (we render `<Sheet side="right">` on
   *  desktop instead — same primitive as mobile). */
  anchorRef?: React.RefObject<HTMLElement | null>
  onInsertAtCursor: (text: string) => void
  onReplaceSelection: (text: string) => void
}

/**
 * Tailwind's `md` breakpoint is 768px. Match it for the desktop /
 * mobile split. SSR-safe: starts as false; the effect upgrades to the
 * real value on mount. Worst case (SSR -> CSR transition on first
 * paint) the mobile layout shows for one frame on desktop, which is
 * imperceptible.
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = () => setIsDesktop(mq.matches)
    handler()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

export function EricInlineDrawer({
  open,
  onOpenChange,
  surface,
  currentText,
  anchorRef,
  onInsertAtCursor,
  onReplaceSelection,
}: EricInlineDrawerProps) {
  const isDesktop = useIsDesktop()
  // Same primitive, two presentations:
  //   - desktop: side="right" — full-height drawer, plenty of room for
  //     a textarea + Eric's tool replies (which can be table-shaped)
  //   - mobile: side="bottom" — keyboard-friendly, swipe-to-dismiss
  // Note: anchorRef is currently unused — kept on the API for a future
  // popover variant if Eric replies turn out to be short enough that a
  // popover would feel snappier.
  void anchorRef
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isDesktop ? 'right' : 'bottom'}
        className={
          isDesktop
            ? 'flex h-dvh w-full flex-col gap-0 p-0 sm:max-w-md'
            : 'flex max-h-[90dvh] flex-col gap-0 p-0'
        }
      >
        <DrawerBody
          surface={surface}
          currentText={currentText}
          onClose={() => onOpenChange(false)}
          onInsertAtCursor={onInsertAtCursor}
          onReplaceSelection={onReplaceSelection}
          mobile={!isDesktop}
        />
      </SheetContent>
    </Sheet>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Shared body — same UX on both modalities. Header (assistant name +
// 3 starter actions) / body (editable prompt + reply) / sticky footer
// (Send). Follows the three-region pattern from CLAUDE.md so action
// buttons stay reachable when the keyboard opens.

function DrawerBody({
  surface,
  currentText,
  onClose,
  onInsertAtCursor,
  onReplaceSelection,
  mobile,
}: {
  surface: EricSurface
  currentText: string
  onClose: () => void
  onInsertAtCursor: (text: string) => void
  onReplaceSelection: (text: string) => void
  mobile?: boolean
}) {
  const { t } = useTranslation('eric_inline')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const assistantName = useAssistantName()
  const [prompt, setPrompt] = useState('')
  const [reply, setReply] = useState<string | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)
  const promptRef = useRef<HTMLTextAreaElement | null>(null)
  // Per-conversation memory key for this inline Eric session — minted once per
  // mount so each Ask-Eric invocation has its own Mastra memory instead of
  // sharing the operator's clerkUserId thread. See worker buildMemoryKey.
  const [conversationId] = useState(() => crypto.randomUUID())

  // Seed the prompt with the starter action's template, scoped by
  // surface kind so the language matches what the mechanic is doing.
  const seed = useCallback(
    (starter: EricStarter) => {
      const next = starterPrompt(starter, surface, currentText, assistantName)
      setPrompt(next)
      setReply(null)
      setErrorText(null)
      // Focus the prompt so the user can edit before sending.
      requestAnimationFrame(() => promptRef.current?.focus())
    },
    [surface, currentText, assistantName],
  )

  const ask = useMutation({
    mutationFn: async (text: string): Promise<AskResponse> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/eric/ask', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, surface, conversation_id: conversationId }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null as unknown)
        const code =
          body && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : null
        if (res.status === 429) throw new Error(t('error.rate_limited'))
        if (code === 'invalid_body') throw new Error(t('error.invalid_body'))
        throw new Error(t('error.generic'))
      }
      return (await res.json()) as AskResponse
    },
    onSuccess: (data) => {
      setReply(data.reply)
      setErrorText(null)
    },
    onError: (err) => {
      setReply(null)
      setErrorText(err instanceof Error ? err.message : String(err))
    },
  })

  const onSend = () => {
    const trimmed = prompt.trim()
    if (trimmed.length === 0 || ask.isPending) return
    ask.mutate(trimmed)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      onSend()
    }
  }

  const starters = useMemo<EricStarter[]>(() => ['help_write', 'summarize', 'lookup'], [])

  return (
    <>
      {/* Sheet (Radix Dialog under the hood) requires a Title and
          Description for accessibility — even when our visible header
          renders the same info. Keep these hidden but present. */}
      <SheetHeader className="sr-only">
        <SheetTitle>{t('title', { name: assistantName })}</SheetTitle>
        <SheetDescription>
          {t('a11y_description', { surface_kind: surface.kind })}
        </SheetDescription>
      </SheetHeader>

      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-brand" />
          <span className="text-sm font-semibold text-foreground">
            {t('title', { name: assistantName })}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label={tCommon('button.close')}
          className="min-h-9 min-w-9"
        >
          <X className="size-4" />
        </Button>
      </header>

      {/* Starter chips. Tapping seeds the prompt — mechanic edits and
          sends. Stacked vertically on mobile so each touch target stays
          comfortably big; row on desktop. */}
      <div className="flex flex-wrap gap-2 border-b border-border bg-muted/30 px-4 py-3 sm:px-5">
        {starters.map((s) => (
          <Button
            key={s}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => seed(s)}
            className="min-h-9"
          >
            {t(`starter.${s}.label`)}
          </Button>
        ))}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:px-5">
        <Textarea
          ref={promptRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('prompt_placeholder', { name: assistantName })}
          rows={mobile ? 3 : 4}
          maxLength={500}
          aria-label={t('prompt_aria_label', { name: assistantName })}
          className="resize-none"
        />

        {ask.isPending ? (
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            {t('thinking', { name: assistantName })}
          </div>
        ) : null}

        {errorText ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
            {errorText}
          </div>
        ) : null}

        {reply ? (
          <div className="space-y-2 rounded-md border border-border bg-background p-3 text-sm text-foreground shadow-sm">
            <div className="whitespace-pre-wrap break-words">{reply}</div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onInsertAtCursor(reply)}
                className="min-h-9"
              >
                <ArrowDownToLine className="size-3.5" />
                {t('insert_at_cursor')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onReplaceSelection(reply)}
                className="min-h-9"
              >
                <RotateCw className="size-3.5" />
                {t('replace_selection')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Sticky footer per CLAUDE.md mobile pattern. On desktop the
          popover is small enough that the sticky/static distinction
          doesn't matter; bg-background gives the bar a visual seam. */}
      <footer
        className={
          'flex items-center justify-end gap-2 border-t border-border bg-background px-4 py-3 sm:px-5 ' +
          (mobile
            ? 'sticky bottom-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
            : '')
        }
      >
        <Button
          type="button"
          onClick={onSend}
          disabled={ask.isPending || prompt.trim().length === 0}
          className="min-h-11 bg-brand text-white hover:bg-brand-dark hover:text-white"
        >
          {ask.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('sending')}
            </>
          ) : (
            <>
              <Send className="size-4" />
              {t('send')}
            </>
          )}
        </Button>
      </footer>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Starter prompts — surface-aware so the mechanic gets a useful seed
// even before editing. Designed to read naturally when sent verbatim,
// not just to be edited.

function starterPrompt(
  starter: EricStarter,
  surface: EricSurface,
  currentText: string,
  _assistantName: string,
): string {
  // _assistantName is kept on the signature for future per-tenant
  // language tweaks ("write me a {{assistantName}}-style note" once
  // we have per-tenant voice cards) but unused today.
  void _assistantName
  // Helper: include the in-progress text so Eric can clean it up or
  // continue from it. Trimmed + capped so we don't blow the 500-char
  // payload limit even if the user has a long note draft.
  const draft = currentText.trim().slice(0, 320)
  const draftClause = draft.length > 0 ? `\n\nMy draft so far:\n${draft}` : ''

  if (starter === 'help_write') {
    switch (surface.kind) {
      case 'customer_notes':
        return `Help me write a brief note for this customer.${draftClause}`
      case 'vehicle_notes':
        return `Help me write an operational note for this vehicle.${draftClause}`
      case 'appointment_notes':
        return `Help me write the job notes for this appointment.${draftClause}`
      case 'messaging_thread':
      case 'dashboard':
      default:
        return `Help me write this.${draftClause}`
    }
  }
  if (starter === 'summarize') {
    switch (surface.kind) {
      case 'customer_notes':
        return `Summarize my recent history with this customer — last few estimates, last few visits, anything notable.`
      case 'vehicle_notes':
        return `Summarize this vehicle's service history — last few estimates, last few appointments, persistent notes, any active carryover flags.`
      case 'appointment_notes':
        return `Summarize what I know about this car and customer before I start.`
      case 'messaging_thread':
      case 'dashboard':
      default:
        return `Summarize what you've got on this for me.`
    }
  }
  // lookup
  switch (surface.kind) {
    case 'customer_notes':
      return `Look something up for this customer — `
    case 'vehicle_notes':
      return `Look up the labor rate or part info for this car — `
    case 'appointment_notes':
      return `Look something up for this appointment — `
    case 'messaging_thread':
    case 'dashboard':
    default:
      return `Look something up — `
  }
}
